import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Users, ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Loader2, Download, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function ExpertCompleteProfile() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, refreshUser, API } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchingOrcid, setFetchingOrcid] = useState(false);
  const [orcidFetched, setOrcidFetched] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  
  const [formData, setFormData] = useState({
    orcid_id: '',
    biography: '',
    employment: '',
    education: '',
    professional_activities: '',
    funding: '',
    featured_works: '',
    works: '',
    has_lab_access: null,
    connected_with_experts: null,
    worked_with_industry: null,
    // University referral extras (populated when expert signed up via a university referral link)
    designation: '',
    department: '',
    school: '',
    employee_id: '',
  });
  const [refToken, setRefToken] = useState('');
  const [refUniName, setRefUniName] = useState('');
  const [fieldOptions, setFieldOptions] = useState({ designation: [], department: [], school: [] });

  // Pick up a university referral token from the URL (`?ref=...`) stashed on landing
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('ref') || localStorage.getItem('collabhive_uni_ref') || '';
      if (t) {
        setRefToken(t);
        localStorage.setItem('collabhive_uni_ref', t);
        const base = process.env.REACT_APP_BACKEND_URL;
        fetch(`${base}/api/universities/by-token/${t}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d && d.kind === 'expert') setRefUniName(d.name); });
        fetch(`${base}/api/universities/by-token/${t}/field-options`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d && d.field_options) setFieldOptions(d.field_options); });
      }
    } catch (e) { /* noop */ }
  }, []);

  // Hydrate form from the user object populated by AuthContext (single source of truth)
  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate('/expert/signup');
      return;
    }
    if (user && !hydrated) {
      setFormData(prev => ({
        ...prev,
        orcid_id: user.orcid_id || '',
        biography: user.biography || '',
        employment: user.employment || '',
        education: user.education || '',
        professional_activities: user.professional_activities || '',
        funding: user.funding || '',
        featured_works: user.featured_works || '',
        works: user.works || '',
        has_lab_access: user.has_lab_access ?? null,
        connected_with_experts: user.connected_with_experts ?? null,
        worked_with_industry: user.worked_with_industry ?? null
      }));
      if (user.orcid_id && user.biography) {
        setOrcidFetched(true);
      }
      setHydrated(true);
    }
  }, [authLoading, user, token, hydrated, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchOrcidData = async () => {
    if (!formData.orcid_id.trim()) {
      toast.error('Please enter your ORCID ID first');
      return;
    }
    
    setFetchingOrcid(true);
    
    try {
      const response = await fetch(`${API}/orcid/fetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orcid_id: formData.orcid_id })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data) {
          setFormData(prev => ({
            ...prev,
            biography: result.data.biography || prev.biography,
            employment: result.data.employment || prev.employment,
            education: result.data.education || prev.education,
            professional_activities: result.data.professional_activities || prev.professional_activities,
            funding: result.data.funding || prev.funding,
            featured_works: result.data.featured_works || prev.featured_works,
            works: result.data.works || prev.works
          }));
          setOrcidFetched(true);
          toast.success('ORCID data extracted successfully!');
        } else {
          toast.error(result.error || 'Failed to fetch ORCID data. Please check your ORCID ID.');
        }
      } else {
        toast.error('Failed to fetch ORCID data');
      }
    } catch (error) {
      toast.error('Failed to connect to ORCID. Please try again.');
    } finally {
      setFetchingOrcid(false);
    }
  };

  const handleSubmit = async () => {
    // If signed up via university ref, require the 4 extra fields
    if (refToken) {
      const missing = ['designation', 'department', 'school', 'employee_id'].filter(k => !formData[k]?.trim());
      if (missing.length) {
        toast.error('Please complete all required university fields (Designation, Department, School, Employee/Student ID)');
        return;
      }
    }
    setLoading(true);
    
    try {
      const payload = { ...formData };
      if (refToken) payload.university_ref_token = refToken;
      const response = await fetch(`${API}/experts/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        refreshUser();
        if (refToken) localStorage.removeItem('collabhive_uni_ref');
        
        if (data.is_approved) {
          toast.success('Profile approved! You are now visible to industry users.');
        } else {
          toast.warning('Profile saved. You need all three "Yes" answers to be visible to industry.');
        }
        
        navigate('/expert/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = formData.orcid_id.trim() !== '' && orcidFetched;
  const canSubmit = formData.has_lab_access !== null && 
                    formData.connected_with_experts !== null && 
                    formData.worked_with_industry !== null;

  if (authLoading || !hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
        </div>
      </nav>

      <div className="container-main py-12">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium">Complete Your Profile</span>
              <span className="text-sm text-muted-foreground">Step {step} of 3</span>
            </div>
            <Progress value={(step / 3) * 100} className="h-2" />
            
            {/* Step indicators */}
            <div className="flex justify-between mt-4">
              {['ORCID', 'Review', 'Qualify'].map((label, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`step-indicator ${
                    step > index + 1 ? 'completed' : 
                    step === index + 1 ? 'active' : 'pending'
                  }`}>
                    {step > index + 1 ? <CheckCircle className="w-4 h-4" /> : index + 1}
                  </div>
                  <span className="text-[10px] sm:text-xs mt-2 text-muted-foreground whitespace-nowrap">
                    <span className="sm:hidden">{label}</span>
                    <span className="hidden sm:inline">
                      {['ORCID Import', 'Review Details', 'Qualification'][index]}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Card className="animate-fade-in">
            <CardHeader>
              <CardTitle>
                {step === 1 && 'Import from ORCID'}
                {step === 2 && 'Review & Edit Details'}
                {step === 3 && 'Qualification Questions'}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Enter your ORCID ID to automatically import your profile data'}
                {step === 2 && 'Review the imported data and make any necessary edits'}
                {step === 3 && 'Answer these questions to complete your profile verification'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Step 1: ORCID Import */}
              {step === 1 && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <Download className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-primary">Automatic Data Import</p>
                        <p className="text-sm text-muted-foreground">
                          Enter your ORCID ID and click "Fetch Data" to automatically import your biography, 
                          employment history, education, publications, funding, and professional activities.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label htmlFor="orcid_id">ORCID ID *</Label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        id="orcid_id"
                        placeholder="0000-0002-1234-5678"
                        value={formData.orcid_id}
                        onChange={(e) => {
                          handleInputChange('orcid_id', e.target.value);
                          setOrcidFetched(false);
                        }}
                        className="flex-1"
                        data-testid="orcid-input"
                      />
                      <Button 
                        onClick={fetchOrcidData}
                        disabled={fetchingOrcid || !formData.orcid_id.trim()}
                        className="gap-2 w-full sm:w-auto shrink-0"
                        data-testid="fetch-orcid-btn"
                      >
                        {fetchingOrcid ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Fetching...
                          </>
                        ) : orcidFetched ? (
                          <>
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Fetch Data
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your ORCID identifier (e.g., 0000-0002-1234-5678). Find it at{' '}
                      <a href="https://orcid.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        orcid.org
                      </a>
                    </p>
                  </div>

                  {orcidFetched && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 animate-fade-in">
                      <div className="flex gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                        <div>
                          <p className="font-medium text-green-800">Data Imported Successfully</p>
                          <p className="text-sm text-green-700">
                            Your ORCID profile data has been extracted. Click "Next" to review and edit.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!orcidFetched && formData.orcid_id && (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
                        <div>
                          <p className="font-medium text-yellow-800">Data Not Yet Fetched</p>
                          <p className="text-sm text-yellow-700">
                            Click "Fetch Data" to import your profile from ORCID.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Review Details */}
              {step === 2 && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-muted/50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-muted-foreground">
                      Review the data imported from ORCID. You can edit any field if needed.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="biography">Biography</Label>
                    <Textarea
                      id="biography"
                      placeholder="Your research interests and background..."
                      value={formData.biography}
                      onChange={(e) => handleInputChange('biography', e.target.value)}
                      rows={4}
                      data-testid="biography-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="employment">Employment</Label>
                    <Textarea
                      id="employment"
                      placeholder="Your employment history..."
                      value={formData.employment}
                      onChange={(e) => handleInputChange('employment', e.target.value)}
                      rows={3}
                      data-testid="employment-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="education">Education</Label>
                    <Textarea
                      id="education"
                      placeholder="Your educational background..."
                      value={formData.education}
                      onChange={(e) => handleInputChange('education', e.target.value)}
                      rows={3}
                      data-testid="education-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="professional_activities">Professional Activities</Label>
                    <Textarea
                      id="professional_activities"
                      placeholder="Committee memberships, editorial roles..."
                      value={formData.professional_activities}
                      onChange={(e) => handleInputChange('professional_activities', e.target.value)}
                      rows={3}
                      data-testid="activities-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="funding">Funding & Grants</Label>
                    <Textarea
                      id="funding"
                      placeholder="Your research funding..."
                      value={formData.funding}
                      onChange={(e) => handleInputChange('funding', e.target.value)}
                      rows={3}
                      data-testid="funding-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="featured_works">Publications</Label>
                    <Textarea
                      id="featured_works"
                      placeholder="Your top publications..."
                      value={formData.featured_works}
                      onChange={(e) => handleInputChange('featured_works', e.target.value)}
                      rows={3}
                      data-testid="featured-works-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="works">Patents</Label>
                    <Textarea
                      id="works"
                      placeholder="Additional publications, patents..."
                      value={formData.works}
                      onChange={(e) => handleInputChange('works', e.target.value)}
                      rows={3}
                      data-testid="works-input"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Qualification Questions */}
              {step === 3 && (
                <div className="space-y-8 animate-fade-in">
                  {refUniName && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4" data-testid="expert-ref-banner">
                      <p className="text-sm">
                        You were invited by <b>{refUniName}</b>. Please fill in your institutional details below —
                        this helps your university report your work for NIRF / NAAC R&amp;D metrics.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label htmlFor="designation">Designation *</Label>
                          {fieldOptions.designation && fieldOptions.designation.length > 0 ? (
                            <Select value={formData.designation} onValueChange={(v) => handleInputChange('designation', v)}>
                              <SelectTrigger data-testid="uni-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
                              <SelectContent>
                                {fieldOptions.designation.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input id="designation" value={formData.designation} onChange={(e) => handleInputChange('designation', e.target.value)} placeholder="e.g., Associate Professor" data-testid="uni-designation" />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="department">Department *</Label>
                          {fieldOptions.department && fieldOptions.department.length > 0 ? (
                            <Select value={formData.department} onValueChange={(v) => handleInputChange('department', v)}>
                              <SelectTrigger data-testid="uni-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                              <SelectContent>
                                {fieldOptions.department.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input id="department" value={formData.department} onChange={(e) => handleInputChange('department', e.target.value)} placeholder="e.g., Chemical Engineering" data-testid="uni-department" />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="school">School / Faculty *</Label>
                          {fieldOptions.school && fieldOptions.school.length > 0 ? (
                            <Select value={formData.school} onValueChange={(v) => handleInputChange('school', v)}>
                              <SelectTrigger data-testid="uni-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                              <SelectContent>
                                {fieldOptions.school.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input id="school" value={formData.school} onChange={(e) => handleInputChange('school', e.target.value)} placeholder="e.g., School of Engineering" data-testid="uni-school" />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="employee_id">Employee / Student ID *</Label>
                          <Input id="employee_id" value={formData.employee_id} onChange={(e) => handleInputChange('employee_id', e.target.value)} placeholder="e.g., EMP-12345" data-testid="uni-emp-id" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
                      <div>
                        <p className="font-medium text-yellow-800">Important</p>
                        <p className="text-sm text-yellow-700">
                          Only profiles with all three "Yes" answers will be approved and visible to industry users.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Question 1 */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">1. Do you have lab access?</Label>
                    <RadioGroup
                      value={formData.has_lab_access === null ? '' : formData.has_lab_access.toString()}
                      onValueChange={(v) => handleInputChange('has_lab_access', v === 'true')}
                      className="flex gap-8"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="lab-yes" data-testid="lab-yes" />
                        <Label htmlFor="lab-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="lab-no" data-testid="lab-no" />
                        <Label htmlFor="lab-no" className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Question 2 */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">2. Are you connected with other experts?</Label>
                    <RadioGroup
                      value={formData.connected_with_experts === null ? '' : formData.connected_with_experts.toString()}
                      onValueChange={(v) => handleInputChange('connected_with_experts', v === 'true')}
                      className="flex gap-8"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="connected-yes" data-testid="connected-yes" />
                        <Label htmlFor="connected-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="connected-no" data-testid="connected-no" />
                        <Label htmlFor="connected-no" className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Question 3 */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">3. Have you worked with industry before?</Label>
                    <RadioGroup
                      value={formData.worked_with_industry === null ? '' : formData.worked_with_industry.toString()}
                      onValueChange={(v) => handleInputChange('worked_with_industry', v === 'true')}
                      className="flex gap-8"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="industry-yes" data-testid="industry-yes" />
                        <Label htmlFor="industry-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="industry-no" data-testid="industry-no" />
                        <Label htmlFor="industry-no" className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Preview */}
                  {formData.has_lab_access !== null && 
                   formData.connected_with_experts !== null && 
                   formData.worked_with_industry !== null && (
                    <div className={`rounded-lg p-4 ${
                      formData.has_lab_access && formData.connected_with_experts && formData.worked_with_industry
                        ? 'bg-primary/10 border border-primary/20'
                        : 'bg-destructive/10 border border-destructive/20'
                    }`}>
                      <div className="flex gap-3">
                        {formData.has_lab_access && formData.connected_with_experts && formData.worked_with_industry ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                            <div>
                              <p className="font-medium text-primary">Profile Will Be Approved</p>
                              <p className="text-sm text-primary/80">
                                Your profile will be visible to industry users after submission.
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                            <div>
                              <p className="font-medium text-destructive">Profile Will Not Be Approved</p>
                              <p className="text-sm text-destructive/80">
                                You need all three "Yes" answers to be visible to industry users.
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setStep(s => s - 1)}
                  disabled={step === 1}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </Button>
                
                {step < 3 ? (
                  <Button
                    onClick={() => setStep(s => s + 1)}
                    disabled={step === 1 && !canProceedStep1}
                    className="gap-2"
                    data-testid="next-step-btn"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit || loading}
                    className="gap-2"
                    data-testid="submit-profile-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Complete Profile
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
